import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Select,
  Switch,
  Button,
  Upload,
  message,
  Row,
  Col,
  Image,
  Space,
  Progress,
  Tag,
  Alert,
  Divider,
  Empty,
  Modal,
} from 'antd';
import {
  InboxOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ScissorOutlined,
} from '@ant-design/icons';
import gifsicle from 'gifsicle-wasm-browser';
import { getCategoryList, batchUploadStickers } from '../services/api';

const { Dragger } = Upload;
const { Option } = Select;

const MAX_FILES = 30;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

const StickerUpload = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  
  // GIF 压缩相关状态
  const [compressionQueue, setCompressionQueue] = useState([]);
  const [currentCompressing, setCurrentCompressing] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  // 监听队列处理 GIF
  useEffect(() => {
    if (compressionQueue.length > 0 && !currentCompressing && !isCompressing) {
      const nextFile = compressionQueue[0];
      processGifCompression(nextFile);
    }
  }, [compressionQueue, currentCompressing, isCompressing]);

  const processGifCompression = async (file) => {
    setIsCompressing(true);
    try {
      // 1. 获取原图预览
      const originalPreview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });

      // 2. 使用 gifsicle 压缩
      // 使用 -O3 优化和 --lossy=10 (有损压缩以减小体积)
      const resultFiles = await gifsicle.run({
        input: [{
          file: file,
          name: "input.gif"
        }],
        command: ["-O3 --lossy=10 --colors=256 --resize-height 128 input.gif -o /out/output.gif"]
      });

      if (!resultFiles || resultFiles.length === 0) {
        throw new Error("Compression returned no files");
      }

      const compressedFile = resultFiles[0];
      
      // 3. 获取压缩后预览
      const compressedPreview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(compressedFile);
      });

      setCurrentCompressing({
        originalFile: file,
        originalPreview,
        originalSize: file.size,
        compressedFile: compressedFile,
        compressedPreview,
        compressedSize: compressedFile.size,
      });
    } catch (error) {
      console.error('GIF compression failed:', error);
      message.error(`${file.name} 压缩失败，将使用原图`);
      // 失败则直接使用原图
      addFileToList(file);
      setCompressionQueue((prev) => prev.slice(1));
    } finally {
      setIsCompressing(false);
    }
  };

  const handleConfirmCompression = () => {
    if (currentCompressing) {
      const finalFile = currentCompressing.compressedFile;
      // 保持原有 uid 或生成新的
      finalFile.uid = currentCompressing.originalFile.uid;
      // 保持原有文件名
      // File 对象的一些属性是只读的，需要通过构造函数或 DefineProperty，但 gifsicle 返回的应该是新的 File
      // 这里需要确保 name 一致，方便后续处理
      const namedFile = new File([finalFile], currentCompressing.originalFile.name, { type: 'image/gif', lastModified: Date.now() });
      namedFile.uid = currentCompressing.originalFile.uid;

      addFileToList(namedFile);
      setCompressionQueue((prev) => prev.slice(1));
      setCurrentCompressing(null);
    }
  };

  const handleUseOriginal = () => {
    if (currentCompressing) {
      addFileToList(currentCompressing.originalFile);
      setCompressionQueue((prev) => prev.slice(1));
      setCurrentCompressing(null);
    }
  };

  const handleSkipFile = () => {
    setCompressionQueue((prev) => prev.slice(1));
    setCurrentCompressing(null);
  };

  const addFileToList = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setFileList((prev) => {
        if (prev.find(f => f.uid === file.uid)) return prev;
        return [...prev, {
          uid: file.uid,
          name: file.name,
          file: file,
          preview: e.target.result,
          type: getFileTypeFromName(file.name),
          size: file.size,
        }];
      });
    };
    reader.readAsDataURL(file);
  };

  const loadCategories = async () => {
    try {
      const data = await getCategoryList();
      setCategories(data || []);
    } catch (error) {
      console.error('加载分类失败', error);
    }
  };

  // 检测文件类型
  const getFileTypeFromName = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'gif') return 'gif';
    return 'static';
  };

  // 处理文件选择
  const handleBeforeUpload = (file, newFileList) => {
    // 检查文件类型
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error(`${file.name} 不是支持的图片格式`);
      return Upload.LIST_IGNORE;
    }

    // 检查文件数量
    const totalFiles = fileList.length + newFileList.filter(f => f.uid !== file.uid).length + 1;
    if (totalFiles > MAX_FILES) {
      message.error(`最多只能上传 ${MAX_FILES} 个文件`);
      return Upload.LIST_IGNORE;
    }

    // 如果是 GIF，进入压缩流程
    if (file.type === 'image/gif') {
      setCompressionQueue((prev) => [...prev, file]);
      return Upload.LIST_IGNORE;
    }

    // 生成预览
    const reader = new FileReader();
    reader.onload = (e) => {
      setFileList((prev) => {
        // 避免重复添加
        if (prev.find(f => f.uid === file.uid)) return prev;
        return [...prev, {
          uid: file.uid,
          name: file.name,
          file: file,
          preview: e.target.result,
          type: getFileTypeFromName(file.name),
          size: file.size,
        }];
      });
    };
    reader.readAsDataURL(file);

    return false; // 阻止自动上传
  };

  // 移除文件
  const handleRemoveFile = (uid) => {
    setFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  // 清空所有文件
  const handleClearAll = () => {
    setFileList([]);
    setUploadResult(null);
  };

  // 提交上传
  const handleSubmit = async () => {
    if (fileList.length === 0) {
      message.error('请选择要上传的文件');
      return;
    }

    try {
      const values = await form.validateFields();

      if (!values.category_ids || values.category_ids.length === 0) {
        message.error('请选择至少一个分类');
        return;
      }

      setUploading(true);
      setUploadProgress(0);
      setUploadResult(null);

      // 构建 FormData
      const formData = new FormData();
      fileList.forEach((item) => {
        formData.append('files', item.file);
      });
      formData.append('category_ids', values.category_ids.join(','));
      if (values.file_type) {
        formData.append('file_type', values.file_type);
      }
      formData.append('is_vip_only', values.is_vip_only ? '1' : '0');

      // 模拟进度
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 500);

      const result = await batchUploadStickers(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResult(result);

      if (result.success_count > 0) {
        message.success(`成功上传 ${result.success_count} 个贴纸`);
      }
      if (result.fail_count > 0) {
        message.warning(`${result.fail_count} 个文件上传失败`);
      }

      // 清空已上传成功的文件
      if (result.success_count === fileList.length) {
        setFileList([]);
      }
    } catch (error) {
      console.error('上传失败', error);
      message.error('上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  // 格式化文件大小
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>贴纸上传</h2>
        <p>批量上传贴纸文件，支持 PNG、JPG、GIF、WebP 格式，一次最多 {MAX_FILES} 个文件</p>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="选择文件" bordered={false}>
            <Dragger
              multiple
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
              disabled={uploading}
              style={{ marginBottom: 16 }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: 48, color: '#667eea' }} />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域</p>
              <p className="ant-upload-hint">
                支持 PNG、JPG、GIF、WebP 格式，一次最多 {MAX_FILES} 个文件
              </p>
            </Dragger>

            {fileList.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span>
                    已选择 <strong>{fileList.length}</strong> 个文件
                    {fileList.length >= MAX_FILES && (
                      <Tag color="warning" style={{ marginLeft: 8 }}>已达上限</Tag>
                    )}
                  </span>
                  <Button danger onClick={handleClearAll} disabled={uploading}>
                    清空全部
                  </Button>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 12,
                  maxHeight: 400,
                  overflowY: 'auto',
                  padding: 8,
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                }}>
                  {fileList.map((item) => (
                    <div
                      key={item.uid}
                      style={{
                        position: 'relative',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid #f0f0f0',
                        background: '#fafafa',
                      }}
                    >
                      <div style={{
                        width: '100%',
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        <Image
                          src={item.preview}
                          alt={item.name}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                          preview={{
                            mask: '预览',
                          }}
                        />
                      </div>
                      <div style={{ padding: '4px 8px', background: '#fff' }}>
                        <div style={{
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginBottom: 2,
                        }}>
                          {item.name}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#999',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                          <span>{formatSize(item.size)}</span>
                          <Tag size="small" color={item.type === 'gif' ? 'purple' : 'cyan'}>
                            {item.type === 'gif' ? 'GIF' : '静态'}
                          </Tag>
                        </div>
                      </div>
                      {!uploading && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveFile(item.uid)}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: 'rgba(255,255,255,0.9)',
                            borderRadius: '50%',
                            padding: '2px 6px',
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {fileList.length === 0 && (
              <Empty description="暂无选择文件" style={{ marginTop: 24 }} />
            )}
          </Card>

          {uploadResult && (
            <Card title="上传结果" bordered={false} style={{ marginTop: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', gap: 16 }}>
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    成功: {uploadResult.success_count}
                  </Tag>
                  {uploadResult.fail_count > 0 && (
                    <Tag icon={<CloseCircleOutlined />} color="error">
                      失败: {uploadResult.fail_count}
                    </Tag>
                  )}
                </div>

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <Alert
                    type="error"
                    message="上传失败的文件"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {uploadResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    }
                  />
                )}

                {uploadResult.success_count > 0 && (
                  <Button type="link" onClick={() => navigate('/stickers')}>
                    查看贴纸列表 →
                  </Button>
                )}
              </Space>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title="上传设置" bordered={false}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                file_type: '',
                is_vip_only: false,
              }}
            >
              <Form.Item
                name="category_ids"
                label="所属分类"
                rules={[{ required: true, message: '请选择至少一个分类' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="请选择分类（可多选）"
                  style={{ width: '100%' }}
                >
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.label_zh || cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="file_type"
                label="文件类型"
                extra="不选择则自动识别，GIF 单帧视为静态图"
              >
                <Select placeholder="自动识别" allowClear>
                  <Option value="">自动识别</Option>
                  <Option value="static">静态图</Option>
                  <Option value="animated">动图</Option>
                </Select>
              </Form.Item>

              <Form.Item name="is_vip_only" label="VIP专属" valuePropName="checked">
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>

              <Divider />

              {uploading && (
                <Progress
                  percent={uploadProgress}
                  status="active"
                  style={{ marginBottom: 16 }}
                />
              )}

              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  block
                  size="large"
                  loading={uploading}
                  disabled={fileList.length === 0}
                  onClick={handleSubmit}
                >
                  {uploading ? '上传中...' : `上传 ${fileList.length} 个文件`}
                </Button>
                <Button
                  block
                  size="large"
                  onClick={() => navigate('/stickers')}
                  disabled={uploading}
                >
                  返回列表
                </Button>
              </Space>
            </Form>
          </Card>

          <Card bordered={false} style={{ marginTop: 16 }}>
            <h4>上传说明</h4>
            <ul style={{ paddingLeft: 20, color: '#666', fontSize: 13 }}>
              <li>支持 PNG、JPG、GIF、WebP 格式</li>
              <li>一次最多上传 {MAX_FILES} 个文件</li>
              <li>文件名将作为贴纸名称（去除扩展名）</li>
              <li>GIF 文件会自动检测帧数，单帧 GIF 视为静态图</li>
              <li>上传前请确认预览无误</li>
            </ul>
          </Card>
        </Col>
      </Row>

      <Modal
        title="GIF 压缩预览"
        open={!!currentCompressing}
        closable={false}
        maskClosable={false}
        width={800}
        footer={[
          <Button key="skip" onClick={handleSkipFile}>
            放弃此图
          </Button>,
          <Button key="original" onClick={handleUseOriginal}>
            使用原图
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmCompression}>
            确认使用压缩版
          </Button>,
        ]}
      >
        {currentCompressing && (
          <div style={{ display: 'flex', gap: 24, padding: '20px 0' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>原图</div>
              <div
                style={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  marginBottom: 8,
                  borderRadius: 4,
                  border: '1px solid #eee',
                }}
              >
                <Image
                  src={currentCompressing.originalPreview}
                  style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                  preview={false}
                />
              </div>
              <div>{formatSize(currentCompressing.originalSize)}</div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <ScissorOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>To</div>
              </div>
            </div>

            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ marginBottom: 8, fontWeight: 'bold' }}>压缩后</div>
              <div
                style={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  marginBottom: 8,
                  borderRadius: 4,
                  border: '1px solid #eee',
                }}
              >
                <Image
                  src={currentCompressing.compressedPreview}
                  style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                  preview={false}
                />
              </div>
              <div style={{ fontWeight: 'bold' }}>
                {formatSize(currentCompressing.compressedSize)}
              </div>
              <div
                style={{
                  color:
                    currentCompressing.compressedSize < currentCompressing.originalSize
                      ? '#52c41a'
                      : '#f5222d',
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {currentCompressing.compressedSize < currentCompressing.originalSize ? '节省 ' : '增加 '}
                {Math.abs(
                  ((currentCompressing.compressedSize - currentCompressing.originalSize) /
                    currentCompressing.originalSize) *
                    100
                ).toFixed(1)}
                %
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StickerUpload;
